"use client";

import * as React from "react";
import {CartesianGrid, XAxis, YAxis, Tooltip, LineChart, Line, ResponsiveContainer} from "recharts";
import {CircleHelp, Download, ChevronDown} from "lucide-react";
import {Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import {ChartContainer} from "@/components/ui/chart";
import {Button} from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {cn} from "@/lib/utils";
import axiosInstance from "@/api/axios";
import {useTranslation} from "react-i18next";

const CHART_COLORS: string[] = [
    "hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))",
    "hsl(var(--chart-4))", "hsl(var(--chart-5))",
];

interface BatteryPoint {
    x: number;
    y: number;
    isAnchor: boolean;
}

interface BatteryChartProps {
    className?: string;
    title?: string;
    timeRange?: string;
    bins?: string[];
    customDateRange?: [{startDate: Date | null; endDate: Date | null; key: string}];
    isCustomDateRange?: boolean;
}

export function BatteryChart({className, title, timeRange:rawTimeRange, bins = [], customDateRange, isCustomDateRange}: BatteryChartProps) {
    const {t} = useTranslation();
    const exportRef = React.useRef<HTMLDivElement>(null);

    // Normalize legacy/stale plural values (e.g. "6 Months" from old cached
    // localStorage) so domain/tick calculations always match the singular keys.
    const timeRange = React.useMemo(() => {
        if (!rawTimeRange) return rawTimeRange;
        return rawTimeRange
            .replace('Months', 'Month')
            .replace('Days', 'Day')
            .replace('Weeks', 'Week')
            .replace('Years', 'Year');
    }, [rawTimeRange]);
    const [seriesByBin, setSeriesByBin] = React.useState<Record<string, BatteryPoint[]>>({});
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const [refreshTick, setRefreshTick] = React.useState(0);
    React.useEffect(() => {
        const interval = setInterval(() => {
            setRefreshTick(tick => tick + 1);
        }, 10 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    React.useEffect(() => {
        setSeriesByBin({});
        setError(null);
        if (!bins || bins.length === 0) return;
        if (!timeRange && !isCustomDateRange) return;

        setLoading(true);
        const params: any = {bins: bins.join(",")};

        if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
            const startDate = customDateRange[0].startDate;
            const endDate = customDateRange[0].endDate;
            params.startDate = startDate instanceof Date ? startDate.toISOString() : startDate;
            params.endDate = endDate instanceof Date ? endDate.toISOString() : endDate;
        } else {
            params.timeRange = timeRange;
        }

        axiosInstance
            .get("/battery-history", {params})
            .then((response) => {
                const raw: Record<string, {timestamp: string; charge_percentage: number}[]> = response.data || {};
                const result: Record<string, BatteryPoint[]> = {};

                Object.entries(raw).forEach(([bin, points]) => {
                    result[bin] = points.map((p, i, arr) => {
                        const date = new Date(p.timestamp);
                        const dayKey = date.toDateString();
                        const isFirstOfDay = i === 0 || new Date(arr[i - 1].timestamp).toDateString() !== dayKey;
                        return {
                            x: date.getTime(),
                            y: p.charge_percentage,
                            isAnchor: isFirstOfDay,
                        };
                    });
                });

                setSeriesByBin(result);
            })
            .catch((err) => {
                console.error("Error fetching battery history:", err);
                setError(t('batteryChart.failedToFetch'));
            })
            .finally(() => setLoading(false));
    }, [timeRange, bins, customDateRange, isCustomDateRange, t, refreshTick]);

    const {rangeStart, rangeEnd} = React.useMemo(() => {
        if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
            const s = customDateRange[0].startDate;
            const e = customDateRange[0].endDate;
            return {
                rangeStart: (s instanceof Date ? s : new Date(s)).getTime(),
                rangeEnd: (e instanceof Date ? e : new Date(e)).getTime(),
            };
        }
        const daysMap: Record<string, number> = {
            'Day': 1, 'Week': 7, 'Month': 30, '3 Month': 90, '6 Month': 180, 'Year': 365,
        };
        const days = daysMap[timeRange || 'Week'] ?? 7;
        const end = Date.now();
        const start = end - days * 24 * 60 * 60 * 1000;
        return {rangeStart: start, rangeEnd: end};
    }, [timeRange, customDateRange, isCustomDateRange]);

    // ── Tick generation: Day = hourly, Week/Month = daily, 3M/6M/Year = monthly ──
    const axisTicks = React.useMemo(() => {
        const ticks: number[] = [];
        const start = new Date(rangeStart);

        if (timeRange === 'Day') {
            start.setMinutes(0, 0, 0);
            const cursor = new Date(start);
            while (cursor.getTime() <= rangeEnd) {
                ticks.push(cursor.getTime());
                cursor.setHours(cursor.getHours() + 1);
            }
        } else if (timeRange === 'Week' || timeRange === 'Month') {
            start.setHours(0, 0, 0, 0);
            const cursor = new Date(start);
            while (cursor.getTime() <= rangeEnd) {
                ticks.push(cursor.getTime());
                cursor.setDate(cursor.getDate() + 1);
            }
        } else {
            // 3 Month, 6 Month, Year, and custom fallback: one tick per month
            start.setDate(1);
            start.setHours(0, 0, 0, 0);
            const cursor = new Date(start);
            while (cursor.getTime() <= rangeEnd) {
                ticks.push(cursor.getTime());
                cursor.setMonth(cursor.getMonth() + 1);
            }
        }

        return ticks;
    }, [rangeStart, rangeEnd, timeRange]);

    const formatXTick = (ms: number): string => {
        const d = new Date(ms);
        if (timeRange === 'Day') {
            return d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
        }
        if (timeRange === 'Week' || timeRange === 'Month') {
            return d.toLocaleDateString([], {month: 'short', day: 'numeric'});
        }
        // 3 Month, 6 Month, Year
        return d.toLocaleDateString([], {month: 'short'});
    };

    // ── Y-axis domain: adapt to the data range but always step by 10% ──
    const {yDomain, yTicks} = React.useMemo<{yDomain: [number, number]; yTicks: number[]}>(() => {
        const values = Object.values(seriesByBin).flat().map(p => p.y);
        if (values.length === 0) return {yDomain: [0, 100], yTicks: [0, 20, 40, 60, 80, 100]};

        const min = Math.min(...values);
        const max = Math.max(...values);
        const step = 5;

        const lo = Math.max(0, Math.floor(min / step) * step - step);
        const hi = Math.min(100, Math.ceil(max / step) * step + step);

        const ticks: number[] = [];
        for (let v = lo; v <= hi; v += step) ticks.push(v);

        return {yDomain: [lo, hi], yTicks: ticks};
    }, [seriesByBin]);

    const onAnchorClick = (bin: string, point: BatteryPoint) => {
        console.log('Anchor clicked:', bin, new Date(point.x), point.y);
    };

    const renderDot = (bin: string, color: string) => (props: any) => {
        const {cx, cy, payload} = props;
        if (!payload?.isAnchor) {
            return <g />;
        }
        return (
            <circle
                cx={cx}
                cy={cy}
                r={5}
                fill={color}
                stroke="white"
                strokeWidth={2}
                style={{cursor: 'pointer'}}
                onClick={() => onAnchorClick(bin, payload)}
            />
        );
    };

    const chartTitle = title || t('batteryChart.title');
    const hasData = Object.values(seriesByBin).some(arr => arr.length > 0);

    const handleExportCSV = () => {
        if (!hasData) return;
        const headers = ["Bin", "Timestamp", "Charge %"];
        const rows: string[] = [];
        Object.entries(seriesByBin).forEach(([bin, points]) => {
            points.forEach((p) => {
                rows.push([bin, new Date(p.x).toISOString(), p.y].join(","));
            });
        });
        const blob = new Blob([[headers.join(","), ...rows].join("\n")], {type: "text/csv;charset=utf-8;"});
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `battery_history_${timeRange || "data"}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
    };

    const handleExportPNG = () => {
        if (!exportRef.current) return;
        const controls = exportRef.current.querySelector(".export-controls") as HTMLElement | null;
        if (controls) controls.style.display = "none";
        import("html2canvas").then((h) => {
            h.default(exportRef.current as HTMLElement, {scale: 2, useCORS: true}).then((canvas) => {
                const link = document.createElement("a");
                link.download = `battery_history_${timeRange || "data"}.png`;
                link.href = canvas.toDataURL("image/png");
                link.click();
            }).finally(() => { if (controls) controls.style.display = ""; });
        });
    };

    if (loading) {
        return (
            <div className={cn("lg:col-span-2 aspect-[16/10] sm:rounded-lg bg-muted/50 p-4", className)}>
                <div className="flex justify-between items-center mb-2">
                    <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{chartTitle}</h4>
                    <div className="flex items-center gap-2 export-controls">
                        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary" disabled>
                            <Download className="h-4 w-4 mr-1" />
                            Export
                            <ChevronDown className="h-4 w-4 ml-1" />
                        </Button>
                        <TooltipProvider>
                            <UITooltip>
                                <TooltipTrigger asChild>
                                    <CircleHelp className="h-5 w-5 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{t('batteryChart.tooltipDescription')}</p>
                                </TooltipContent>
                            </UITooltip>
                        </TooltipProvider>
                    </div>
                </div>

                {/* Skeleton — same shape as the loaded LineChart: y-axis %
                    ticks, gridlines, a couple of wavy lines standing in for
                    per-bin battery trends, and x-axis ticks. */}
                <div
                    className="px-2 pt-4 sm:px-6 sm:pt-6 h-full w-full flex motion-safe:animate-pulse"
                    aria-busy="true"
                >
                    <span className="sr-only" role="status">{t('batteryChart.loading')}</span>

                    <div className="flex flex-col justify-between py-2 pr-2 w-10">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-2 w-full rounded bg-gray-200 dark:bg-slate-700" />
                        ))}
                    </div>

                    <div className="flex-1 flex flex-col min-w-0">
                        <div className="relative flex-1">
                            <div className="absolute inset-0 flex flex-col justify-between py-2">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <div key={i} className="h-px w-full bg-gray-200 dark:bg-slate-700" />
                                ))}
                            </div>
                            <svg
                                viewBox="0 0 100 40"
                                preserveAspectRatio="none"
                                className="absolute inset-0 w-full h-full"
                                aria-hidden="true"
                            >
                                {[
                                    "M0,14 C15,10 25,18 35,12 C45,8 55,16 65,10 C75,6 85,14 100,8",
                                    "M0,26 C15,30 25,22 35,28 C45,20 55,26 65,18 C75,22 85,14 100,20",
                                ].map((d, i) => (
                                    <path
                                        key={i}
                                        d={d}
                                        fill="none"
                                        className="stroke-gray-300 dark:stroke-slate-600"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                ))}
                            </svg>
                        </div>

                        <div className="flex justify-between pt-2 px-1">
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className="h-2 w-8 rounded bg-gray-200 dark:bg-slate-700" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cn("lg:col-span-2 aspect-[16/10] sm:rounded-lg bg-muted/50 p-4", className)}>
                <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{chartTitle}</h4>
                <div className="flex h-full items-center justify-center text-destructive">{error}</div>
            </div>
        );
    }

    return (
        <div ref={exportRef} className={cn("lg:col-span-2 aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section", className)}>
            <div className="flex justify-between items-center mb-2">
                <div>
                    <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{chartTitle}</h4>
                    {isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate && (
                        <p className="text-sm text-muted-foreground mt-0.5">
                            {new Date(customDateRange[0].startDate).toLocaleDateString()} –{" "}
                            {new Date(customDateRange[0].endDate).toLocaleDateString()}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2 export-controls">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"
                                    className="text-muted-foreground hover:text-primary"
                                    disabled={!hasData}>
                                <Download className="h-4 w-4 mr-1" />
                                Export
                                <ChevronDown className="h-4 w-4 ml-1" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleExportCSV}>Download as CSV</DropdownMenuItem>
                            <DropdownMenuItem onClick={handleExportPNG}>Download as PNG</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <TooltipProvider>
                        <UITooltip>
                            <TooltipTrigger asChild>
                                <CircleHelp className="h-5 w-5 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{t('batteryChart.tooltipDescription')}</p>
                            </TooltipContent>
                        </UITooltip>
                    </TooltipProvider>
                </div>
            </div>

            <div className="px-2 pt-4 sm:px-6 sm:pt-6" style={{width: '100%', height: '100%'}}>
                {!hasData ? (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        {t('batteryChart.noDataAvailable')}
                    </div>
                ) : (
                    <ChartContainer config={{}} className="h-full w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart>
                                <CartesianGrid vertical={false} stroke="lightgrey" />
                                <XAxis
                                    dataKey="x"
                                    type="number"
                                    domain={[rangeStart, rangeEnd]}
                                    ticks={axisTicks}
                                    tickFormatter={formatXTick}
                                    tickLine={false}
                                    axisLine={false}
                                    tickMargin={8}
                                    angle={timeRange === 'Day' || timeRange === 'Week' ? -45 : 0}
                                    textAnchor={timeRange === 'Day' || timeRange === 'Week' ? 'end' : 'middle'}
                                    height={timeRange === 'Day' || timeRange === 'Week' ? 80 : 60}
                                    interval={timeRange === 'Month' ? 'preserveStartEnd' : 0}
                                />
                                <YAxis
                                    dataKey="y"
                                    type="number"
                                    domain={yDomain}
                                    ticks={yTicks}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(v) => `${v}%`}
                                />
                                <Tooltip
                                    cursor={{strokeDasharray: "3 3"}}
                                    labelFormatter={(ms) => new Date(ms as number).toLocaleString()}
                                    formatter={(value: number, name: string) => [`${value}%`, `${t('bin')} ${name}`]}
                                />
                                {Object.entries(seriesByBin).map(([bin, points], index) => {
                                    const color = CHART_COLORS[index % CHART_COLORS.length];
                                    return (
                                        <Line
                                            key={bin}
                                            data={points}
                                            dataKey="y"
                                            name={bin}
                                            type="monotone"
                                            stroke={color}
                                            strokeWidth={2}
                                            dot={renderDot(bin, color)}
                                            activeDot={false}
                                            connectNulls
                                        />
                                    );
                                })}
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                )}
            </div>
        </div>
    );
}